import React, {useEffect, useMemo} from "react";
import {debounceTime, EMPTY, Subject, switchMap, tap} from "rxjs";
import {CircularProgress, IconButton, InputAdornment, TextField} from "@mui/material";
import type {PageResponse} from "../../../services/ApiTypes";
import type {Option, OptionPage} from "./ApiDropdownTypes";
import {Clear, Search} from "@mui/icons-material";

type Props = {
    options: OptionPage | null
    getOptions: (search: string) => (Promise<PageResponse<Option>> | null)
    setOptions: (options: OptionPage) => void
    isLoading: boolean
    setIsLoading: (loading: boolean) => void
    onPressEnter?: () => void
}

export const ApiDropdownSearchComponent: React.FC<Props> = ({
                                                                options,
                                                                getOptions,
                                                                setOptions,
                                                                isLoading,
                                                                setIsLoading,
                                                                onPressEnter
                                                            }) => {
    const [inputValue, setInputValue] = React.useState(options?.search ?? '');

    const pipe = useMemo(() => new Subject<string>(), [])

    const handleChange = (v: string) => {
        setInputValue(v);
        pipe.next(v);
    }

    useEffect(() => {
        const sub = pipe.pipe(
            tap(() => setIsLoading(true)),
            // throttleTime(500),
            debounceTime(500),
            switchMap(search => {
                const p = getOptions(search);
                if (!p) {
                    console.warn('User is searching but no getOptions function provided');
                    setIsLoading(false);
                    return EMPTY;
                }
                return p
                    .then(resp => ([resp, search] as const))
                    .finally(() => setIsLoading(false));
            }),
        ).subscribe({
            next: ([resp, search]) => {
                setOptions({
                    search,
                    page: 1,
                    window: resp.results,
                    total: resp.count
                });
            },
            error: err => {
                console.error(err);
            },
        });
        return () => {
            sub.unsubscribe();
            setIsLoading(false);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [setOptions, setIsLoading]);

    return <TextField
        variant="standard"
        fullWidth
        autoFocus
        label="Search"
        value={inputValue}
        onChange={e => handleChange(e.target.value)}
        InputProps={{
            startAdornment: <InputAdornment position="start" style={{width: '1em', minWidth: '1em'}}>
                {isLoading ? <CircularProgress size="1em"/> : <Search/>}
            </InputAdornment>,
            endAdornment: <InputAdornment position="end">
                {inputValue && <IconButton size="small" onClick={() => handleChange('')}><Clear/></IconButton>}
            </InputAdornment>
        }}
        onKeyDown={event => {
            if (event.key === 'Enter' && !isLoading) {
                if (onPressEnter)
                    onPressEnter()
            }
        }}
    />
}
